import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["objectId", "objectTypeId", "renderButton", "placement", "data-test-id"];
import FormattedMessage from 'I18n/components/FormattedMessage';
import { List } from 'foundations-components/transitional/components/List';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { useMemo } from 'react';
import { useValidateObjectForConversion } from './hooks/useValidateObjectForConversion';
import { ObjectConversionError } from './types/validation';
import { DEAL_TYPE_ID, INVOICE_TYPE_ID, QUOTE_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { LineItemValidationMessages } from './LineItemValidationMessages';
import { assertNever } from '../../utils/common';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function ConvertToSubscriptionValidationTooltip(_ref) {
  let {
      objectId,
      objectTypeId,
      renderButton,
      placement = 'left',
      'data-test-id': dataTestId = 'convert-to-subscription-validation-tooltip'
    } = _ref,
    tooltipProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    loading,
    objectConversionErrors,
    lineItemConversionErrors
  } = useValidateObjectForConversion({
    objectTypeId,
    objectId
  });
  const conversionTooltipProps = useMemo(() => {
    if (objectConversionErrors.size > 0) {
      for (const errorMessage of objectConversionErrors) {
        switch (errorMessage) {
          case ObjectConversionError.STILL_LOADING:
            return {
              disabled: false,
              title: /*#__PURE__*/_jsx(FormattedMessage, {
                "data-test-id": `convert-validation-tooltip:${errorMessage}`,
                message: "subscriptions.ConvertToSubscription.validationMessages.loading"
              })
            };
          case ObjectConversionError.NO_CREATE_ACCESS:
            return {
              disabled: false,
              title: /*#__PURE__*/_jsx(FormattedMessage, {
                "data-test-id": `convert-validation-tooltip:${errorMessage}`,
                message: "subscriptions.ConvertToSubscription.validationMessages.missingPermission.missingWritePermission"
              })
            };
          case ObjectConversionError.UNKNOWN_ERROR:
            return {
              disabled: true
            };
          case ObjectConversionError.OBJECT_NOT_FOUND:
          case ObjectConversionError.NO_OBJECT_TYPE:
          case ObjectConversionError.NO_OBJECT_ID:
          case ObjectConversionError.UNSUPPORTED_OBJECT_TYPE:
            return {
              disabled: false,
              title: /*#__PURE__*/_jsx(FormattedMessage, {
                "data-test-id": `convert-validation-tooltip:${errorMessage}`,
                message: "subscriptions.ConvertToSubscription.validationMessages.unsupportedObjectType"
              })
            };
          default:
            assertNever(errorMessage, 'ObjectConversionError');
            return {
              disabled: true
            };
        }
      }
    }
    if (lineItemConversionErrors.size > 0) {
      return {
        disabled: false,
        use: 'longform',
        headingText: /*#__PURE__*/_jsxs(_Fragment, {
          children: [objectTypeId === DEAL_TYPE_ID && /*#__PURE__*/_jsx(FormattedMessage, {
            "data-test-id": "convert-validation-tooltip:cannot-convert-deal",
            message: "subscriptions.ConvertToSubscription.validationMessages.cantConvertFromDeal"
          }), objectTypeId === INVOICE_TYPE_ID && /*#__PURE__*/_jsx(FormattedMessage, {
            "data-test-id": "convert-validation-tooltip:cannot-convert-invoice",
            message: "subscriptions.ConvertToSubscription.validationMessages.cantConvertFromInvoice"
          }), objectTypeId === QUOTE_TYPE_ID && /*#__PURE__*/_jsx(FormattedMessage, {
            "data-test-id": "convert-validation-tooltip:cannot-convert-quote",
            message: "subscriptions.ConvertToSubscription.validationMessages.cantConvertFromQuote"
          })]
        }),
        title: /*#__PURE__*/_jsx(List, {
          styled: true,
          wrapChildren: true,
          children: /*#__PURE__*/_jsx(LineItemValidationMessages, {
            lineItemConversionErrors: lineItemConversionErrors
          })
        })
      };
    }
    return {
      disabled: true
    };
  }, [objectConversionErrors, lineItemConversionErrors, objectTypeId]);
  return /*#__PURE__*/_jsx(Tooltip, Object.assign({
    "data-test-id": dataTestId,
    placement: placement
  }, tooltipProps, conversionTooltipProps, {
    children: /*#__PURE__*/_jsx(_Fragment, {
      children: renderButton({
        loading,
        disabled: !conversionTooltipProps.disabled
      })
    })
  }));
}